import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';

interface TableData {
  headers: string[];
  rows: string[][];
}

const DEFAULT_COLS = 3;
const DEFAULT_ROWS = 3;
const DEFAULT_HEADER = 'Column';
const DEFAULT_CELL = '';

function buildDefaultTable(): TableData {
  return {
    headers: Array.from({ length: DEFAULT_COLS }, (_, i) => `${DEFAULT_HEADER} ${i + 1}`),
    rows: Array.from({ length: DEFAULT_ROWS }, () => Array.from({ length: DEFAULT_COLS }, () => DEFAULT_CELL)),
  };
}

function parseTableContent(content: string): TableData {
  try {
    const parsed = typeof content === 'string' ? JSON.parse(content) : content;
    if (parsed && Array.isArray(parsed.headers) && Array.isArray(parsed.rows)) {
      return parsed as TableData;
    }
  } catch {}
  return buildDefaultTable();
}

@Component({
  selector: 'app-table-chunk-editor',
  imports: [ReactiveFormsModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="border border-[var(--skz-border-color-main)] rounded-[var(--skz-radius-main)] overflow-hidden bg-[var(--skz-background-color-surface)]"
      (keydown)="onKeydown($event)"
    >
      <div class="custom-scrollbar w-full overflow-x-auto">
        <table class="w-full border-collapse min-w-[400px] overflow-hidden relative" [formGroup]="form">
          <thead>
            <tr class="border-b border-[var(--skz-border-color-main)]">
              <th class="w-12 bg-[var(--skz-background-color-primary)]"></th>
              @for (header of headers.controls; track $index; let colIdx = $index) {
                <th class="relative p-1 border-r border-[var(--skz-border-color-secondary)] group min-w-[120px]">
                  <!-- Insert column trigger on the left (only for first column) -->
                  @if (colIdx === 0) {
                    <div
                      class="absolute top-0 left-0 h-full w-2 -translate-x-1/2 cursor-pointer group/col-insert z-30"
                      (click)="addColumnAt(0)"
                      [matTooltip]="'Chunk.Table.InsertColumn' | translate"
                    >
                      <div
                        class="absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-[9999px] bg-[#5a73c5] opacity-0 group-hover/col-insert:opacity-100 transition-opacity pointer-events-none"
                      ></div>
                      <div
                        class="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#5a73c5] text-white flex items-center justify-center opacity-0 group-hover/col-insert:opacity-100 transition-opacity shadow-md pointer-events-none"
                      >
                        <mat-icon class="!text-[12px] !w-[12px] !h-[12px] !leading-[12px] font-bold">add</mat-icon>
                      </div>
                    </div>
                  }

                  <!-- Insert column trigger on the right (except last column) -->
                  @if (colIdx < headers.controls.length - 1) {
                    <div
                      class="absolute top-0 right-0 h-full w-2 translate-x-1/2 cursor-pointer group/col-insert z-30"
                      (click)="addColumnAt(colIdx + 1)"
                      [matTooltip]="'Chunk.Table.InsertColumn' | translate"
                    >
                      <div
                        class="absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-[9999px] bg-[#5a73c5] opacity-0 group-hover/col-insert:opacity-100 transition-opacity pointer-events-none"
                      ></div>
                      <div
                        class="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#5a73c5] text-white flex items-center justify-center opacity-0 group-hover/col-insert:opacity-100 transition-opacity shadow-md pointer-events-none"
                      >
                        <mat-icon class="!text-[12px] !w-[12px] !h-[12px] !leading-[12px] font-bold">add</mat-icon>
                      </div>
                    </div>
                  }

                  <input
                    type="text"
                    [formControl]="asControl(header)"
                    class="w-full bg-transparent border-none outline-none pl-3 pr-8 py-2 text-[13px] font-semibold text-[var(--skz-text-primary)] focus:bg-[rgba(90,115,197,0.04)] rounded-sm transition-colors"
                    [attr.data-row]="-1"
                    [attr.data-col]="colIdx"
                    [placeholder]="'Chunk.Table.ColumnPlaceholder' | translate: { index: colIdx + 1 }"
                  />
                  @if (headers.controls.length > 1) {
                    <button
                      mat-icon-button
                      class="!absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 leading-6"
                      (click)="removeColumn(colIdx, $event)"
                      [matTooltip]="'Chunk.Table.RemoveColumn' | translate"
                    >
                      <mat-icon class="!text-[14px] !w-[14px] !h-[14px] !leading-[14px] text-[var(--skz-text-danger)]"> delete_outline </mat-icon>
                    </button>
                  }
                </th>
              }
              <th class="w-10 p-2">
                <button mat-icon-button (click)="addColumn()" [matTooltip]="'Chunk.Table.AddColumn' | translate">
                  <mat-icon>add</mat-icon>
                </button>
              </th>
            </tr>
          </thead>
          <tbody formArrayName="rows">
            @for (row of rows.controls; track $index; let rowIdx = $index) {
              <tr [formGroupName]="rowIdx" class="group relative border-b border-[var(--skz-border-color-secondary)] last:border-none hover:bg-gray-50 transition-colors">
                <td class="w-12 text-center bg-[var(--skz-background-color-primary)] text-xs text-gray-400 font-medium p-1">
                  <!-- Insert row trigger on top (only for first row) -->
                  @if (rowIdx === 0) {
                    <div
                      class="absolute top-0 left-0 w-full h-2 -translate-y-1/2 cursor-pointer group/row-insert z-30"
                      (click)="addRowAt(0)"
                      [matTooltip]="'Chunk.Table.InsertRow' | translate"
                    >
                      <div
                        class="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] w-full bg-[#5a73c5] opacity-0 group-hover/row-insert:opacity-100 transition-opacity pointer-events-none"
                      ></div>
                      <div
                        class="absolute left-9 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[#5a73c5] text-white flex items-center justify-center opacity-0 group-hover/row-insert:opacity-100 transition-opacity shadow-md pointer-events-none"
                      >
                        <mat-icon class="!text-[12px] !w-[12px] !h-[12px] !leading-[12px] font-bold">add</mat-icon>
                      </div>
                    </div>
                  }

                  <!-- Insert row trigger on bottom (except last row) -->
                  @if (rowIdx < rows.controls.length - 1) {
                    <div
                      class="absolute bottom-0 left-0 w-full h-2 translate-y-1/2 cursor-pointer group/row-insert z-30"
                      (click)="addRowAt(rowIdx + 1)"
                      [matTooltip]="'Chunk.Table.InsertRow' | translate"
                    >
                      <div
                        class="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] w-full bg-[#5a73c5] opacity-0 group-hover/row-insert:opacity-100 transition-opacity pointer-events-none"
                      ></div>
                      <div
                        class="absolute left-9 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[#5a73c5] text-white flex items-center justify-center opacity-0 group-hover/row-insert:opacity-100 transition-opacity shadow-md pointer-events-none"
                      >
                        <mat-icon class="!text-[12px] !w-[12px] !h-[12px] !leading-[12px] font-bold">add</mat-icon>
                      </div>
                    </div>
                  }

                  <span class="leading-8">{{ rowIdx + 1 }}</span>
                </td>
                @for (cell of asFormArray(row).controls; track $index; let colIdx = $index) {
                  <td class="p-1 border-r border-[var(--skz-border-color-secondary)]">
                    <input
                      type="text"
                      [formControl]="asControl(cell)"
                      class="w-full bg-transparent border-none outline-none px-3 py-2 text-sm text-[var(--skz-text-primary)] focus:bg-[rgba(90,115,197,0.04)] rounded-sm transition-colors"
                      [attr.data-row]="rowIdx"
                      [attr.data-col]="colIdx"
                      placeholder=""
                    />
                  </td>
                }
                <td class="w-10 p-2">
                  @if (rows.controls.length > 1) {
                    <button
                      mat-icon-button
                      class="opacity-0 group-hover:opacity-100 transition-opacity"
                      (click)="removeRow(rowIdx, $event)"
                      [matTooltip]="'Chunk.Table.RemoveRow' | translate"
                    >
                      <mat-icon> delete_outline </mat-icon>
                    </button>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div
        class="flex items-center justify-center gap-1 py-2 border-t border-[var(--skz-border-color-secondary)] cursor-pointer hover:bg-[var(--skz-highlighted-item-color-lighter)] transition-colors"
        (click)="addRow()"
      >
        <mat-icon class="!text-[16px] !w-[16px] !h-[16px] text-[var(--skz-text-muted)]">add</mat-icon>
        <span class="text-xs text-[var(--skz-text-muted)]">{{ 'Chunk.Table.AddRow' | translate }}</span>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
})
export class TableChunkEditorComponent {
  content = input<string>('');
  contentChange = output<string>();

  private elementRef = inject(ElementRef);
  private confirmMenuService = inject(ConfirmMenuService);

  form = new FormGroup({
    headers: new FormArray<FormControl<string | null>>([]),
    rows: new FormArray<FormArray<FormControl<string | null>>>([]),
  });

  private suppressEmit = false;
  private lastEmitted = '';

  get headers(): FormArray<FormControl<string | null>> {
    return this.form.get('headers') as FormArray;
  }

  get rows(): FormArray<FormArray<FormControl<string | null>>> {
    return this.form.get('rows') as FormArray<FormArray<FormControl<string | null>>>;
  }

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (!this.suppressEmit) {
        this.emitContent();
      }
    });

    effect(() => {
      const raw = this.content();
      if (raw !== this.lastEmitted) {
        this.buildForm(raw);
      }
    });
  }

  private buildForm(raw: string): void {
    this.suppressEmit = true;

    const data = parseTableContent(raw);

    this.headers.clear();
    data.headers.forEach((h) => this.headers.push(new FormControl(h)));

    this.rows.clear();
    data.rows.forEach((row) => {
      const rowArray = new FormArray<FormControl<string | null>>([]);
      row.forEach((cell) => rowArray.push(new FormControl(cell)));
      this.rows.push(rowArray);
    });

    this.suppressEmit = false;
  }

  private emitContent(): void {
    const data: TableData = {
      headers: this.headers.value.map((v) => v ?? ''),
      rows: this.rows.value.map((row) => (Array.isArray(row) ? row.map((v) => v ?? '') : [])),
    };
    const json = JSON.stringify(data);
    this.lastEmitted = json;
    this.contentChange.emit(json);
  }

  asControl(ctrl: FormControl<string | null>): FormControl<string | null> {
    return ctrl;
  }

  asFormArray(ctrl: FormArray<FormControl<string | null>>): FormArray<FormControl<string | null>> {
    return ctrl;
  }

  addRow(): void {
    this.addRowAt(this.rows.controls.length);
  }

  addRowAt(index: number): void {
    const colCount = this.headers.controls.length || 1;
    const rowArray = new FormArray<FormControl<string | null>>([]);
    for (let i = 0; i < colCount; i++) {
      rowArray.push(new FormControl(''));
    }
    this.rows.insert(index, rowArray);
    this.emitContent();
  }

  removeRow(index: number, event: MouseEvent): void {
    if (this.rows.controls.length <= 1) return;
    this.confirmMenuService.confirm(event.target as HTMLElement, 'Global.Confirm').subscribe(() => {
      this.rows.removeAt(index);
      this.emitContent();
    });
  }

  addColumn(): void {
    this.addColumnAt(this.headers.controls.length);
  }

  addColumnAt(index: number): void {
    this.headers.insert(index, new FormControl(`Column ${this.headers.controls.length + 1}`));
    this.rows.controls.forEach((row) => {
      (row as FormArray).insert(index, new FormControl(''));
    });
    this.emitContent();
  }

  removeColumn(index: number, event: MouseEvent): void {
    if (this.headers.controls.length <= 1) return;
    this.confirmMenuService.confirm(event.target as HTMLElement, 'Global.Confirm').subscribe(() => {
      this.headers.removeAt(index);
      this.rows.controls.forEach((row) => {
        (row as FormArray).removeAt(index);
      });
      this.emitContent();
    });
  }

  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (!target || target.tagName !== 'INPUT') return;

    const row = parseInt(target.getAttribute('data-row') ?? '', 10);
    const col = parseInt(target.getAttribute('data-col') ?? '', 10);
    if (isNaN(row) || isNaN(col)) return;

    let nextRow = row;
    let nextCol = col;

    switch (event.key) {
      case 'ArrowUp':
        nextRow = Math.max(-1, row - 1);
        break;
      case 'ArrowDown':
        nextRow = Math.min(this.rows.controls.length - 1, row + 1);
        break;
      case 'ArrowLeft':
        if (col > 0) {
          nextCol = col - 1;
        } else if (row >= 0 && this.headers.controls.length > 0) {
          nextRow = row - 1;
          nextCol = this.headers.controls.length - 1;
        }
        break;
      case 'ArrowRight':
        if (col < this.headers.controls.length - 1) {
          nextCol = col + 1;
        } else if (row < this.rows.controls.length - 1) {
          nextRow = row + 1;
          nextCol = 0;
        }
        break;
      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          if (col > 0) {
            nextCol = col - 1;
          } else if (row >= 0) {
            nextRow = row - 1;
            nextCol = this.headers.controls.length - 1;
          }
        } else {
          if (col < this.headers.controls.length - 1) {
            nextCol = col + 1;
          } else if (row < this.rows.controls.length - 1) {
            nextRow = row + 1;
            nextCol = 0;
          }
        }
        break;
      case 'Enter':
        if (row === -1) {
          nextRow = 0;
        } else if (row === this.rows.controls.length - 1) {
          this.addRow();
          nextRow = row + 1;
        } else {
          nextRow = row + 1;
        }
        break;
      default:
        return;
    }

    event.preventDefault();
    this.focusCell(nextRow, nextCol);
  }

  private focusCell(row: number, col: number): void {
    requestAnimationFrame(() => {
      const selector = row === -1 ? `input[data-row="-1"][data-col="${col}"]` : `input[data-row="${row}"][data-col="${col}"]`;
      const input = this.elementRef.nativeElement.querySelector(selector) as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.select();
      }
    });
  }
}
